import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import BrandLockup from './BrandMark';

/**
 * 登录后协议门禁：未同意或版本变更时阻断进入主界面
 */
export default function ConsentGate({ children }) {
  const [checking, setChecking] = useState(true);
  const [needsConsent, setNeedsConsent] = useState(false);
  const [versions, setVersions] = useState(null);
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const reload = async () => {
    const status = await api.compliance.consentStatus();
    setNeedsConsent(!!status.needsConsent);
    setVersions(status.current);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let lastErr;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          await reload();
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
          await new Promise((r) => setTimeout(r, 600 * (attempt + 1)));
        }
      }
      if (!cancelled && lastErr) setError(lastErr.message || '合规状态加载失败');
      if (!cancelled) setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAgree = async () => {
    if (!agree || !versions) return;
    setSubmitting(true);
    setError('');
    try {
      let lastErr;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          await api.compliance.agree({
            privacyVersion: versions.privacyVersion,
            termsVersion: versions.termsVersion,
            piListVersion: versions.piListVersion,
          });
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
          await new Promise((r) => setTimeout(r, 600 * (attempt + 1)));
        }
      }
      if (lastErr) throw lastErr;
      setNeedsConsent(false);
    } catch (e) {
      setError(e.message || '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <div className="app-shell flex items-center justify-center !pb-0">
        <div className="text-center">
          <div className="mb-3 flex justify-center">
            <BrandLockup size="md" tone="primary" />
          </div>
        </div>
      </div>
    );
  }

  if (!needsConsent) return children;

  return (
    <div className="app-shell flex items-center justify-center !pb-0 px-4">
      <div className="w-full max-w-md soft-panel !p-6">
        <h1 className="font-display text-xl font-semibold text-neutral-800 dark:text-dark-text mb-2">
          使用前请确认
        </h1>
        <p className="text-sm text-neutral-500 dark:text-dark-muted mb-5 leading-relaxed">
          为保障你的知情权，请阅读并同意以下文件。协议更新后需重新确认。
        </p>
        <ul className="space-y-2.5 text-sm mb-5">
          <li>
            <Link className="text-primary-600 dark:text-primary-400 font-medium underline-offset-2 hover:underline" to="/legal/privacy" target="_blank">
              《隐私政策》
            </Link>
            <span className="text-neutral-400 ml-1.5 text-xs">v{versions?.privacyVersion}</span>
          </li>
          <li>
            <Link className="text-primary-600 dark:text-primary-400 font-medium underline-offset-2 hover:underline" to="/legal/terms" target="_blank">
              《用户协议》
            </Link>
            <span className="text-neutral-400 ml-1.5 text-xs">v{versions?.termsVersion}</span>
          </li>
          <li>
            <Link className="text-primary-600 dark:text-primary-400 font-medium underline-offset-2 hover:underline" to="/legal/pi-list" target="_blank">
              《个人信息收集清单》
            </Link>
            <span className="text-neutral-400 ml-1.5 text-xs">v{versions?.piListVersion}</span>
          </li>
        </ul>
        <label className="flex items-start gap-2.5 text-sm text-neutral-700 dark:text-dark-text mb-5 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 accent-[var(--theme-primary)]"
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
          />
          <span>我已阅读并同意上述文件</span>
        </label>
        {error && <p className="text-sm text-danger-500 mb-3">{error}</p>}
        <button
          type="button"
          disabled={!agree || submitting}
          onClick={handleAgree}
          className="btn-primary w-full disabled:opacity-40"
        >
          {submitting ? '提交中…' : '同意并继续'}
        </button>
      </div>
    </div>
  );
}
